/**
 * La décision annuelle de l'associé unique d'une EURL ou d'une SASU : il
 * approuve les comptes de l'exercice et décide de l'affectation du résultat.
 *
 * UN DOCUMENT D'UNE PAGE QUE BEAUCOUP PAIENT. Chaque année, dans les six mois
 * qui suivent la clôture, l'associé unique statue sur les comptes, et sa
 * décision se consigne au registre des décisions. Le document suit l'ordre
 * habituel d'un procès-verbal : l'identification de la société et de
 * l'associé, l'approbation, l'affectation, les conventions, les pouvoirs pour
 * les formalités.
 *
 * L'AFFECTATION EST CALCULÉE, PAS RECOPIÉE. La réserve légale, le bénéfice
 * distribuable et le report à nouveau sortent de soleShareholderAffectation.js :
 * le lecteur donne son résultat et ce qu'il veut se verser, le document pose
 * les montants que la loi impose.
 *
 * PAS DE QUITUS. La formule « l'associé unique donne quitus au gérant » se lit
 * dans beaucoup de modèles ; elle n'éteint aucune action en responsabilité
 * (code de commerce, article L. 223-22 pour la SARL, L. 225-253 par renvoi
 * pour la SAS). Le document ne l'écrit pas, et la page explique pourquoi.
 *
 * LES MONTANTS SONT ÉCRITS EN TOUTES LETTRES D'EUROS dans les phrases
 * (eurosEnPhrase) : le symbole n'existe pas dans l'encodage du PDF ailleurs
 * qu'en fin de ligne.
 */

import {
    adressePostale,
    dateEnToutesLettres,
    eurosEnPhrase,
    identite,
    nettoyer,
} from "./documentText.js";
import { affecterResultat } from "./soleShareholderAffectation.js";

export const FORMES_ASSOCIE_UNIQUE = {
    eurl: {
        forme: "Société à responsabilité limitée à associé unique",
        dirigeant: "gérant",
    },
    sasu: {
        forme: "Société par actions simplifiée unipersonnelle",
        dirigeant: "président",
    },
};

/**
 * Une SASU est à l'impôt sur les sociétés ; une EURL dont l'associé est une
 * personne physique ne l'est que sur option. Le rappel de l'article 243 bis
 * ne vaut que pour une société à l'IS.
 */
export function aLImpotSurLesSocietes(r) {
    return r?.forme === "sasu" || (r?.forme === "eurl" && r?.fiscal === "is");
}

/** Le délai de mise en paiement des dividendes : neuf mois après la clôture. */
export const MOIS_MISE_EN_PAIEMENT = 9;

/** Le SIREN, neuf chiffres. */
export function siren(brut) {
    const n = nettoyer(brut).replace(/\s/g, "");
    if (!n) return { valeur: "", motif: "vide" };
    if (!/^\d{9}$/.test(n)) return { valeur: n, motif: "format" };
    return { valeur: n, motif: null };
}

/** Un montant saisi, en euros : nombre fini ou `null`. */
function montant(valeur) {
    if (valeur === "" || valeur === null || valeur === undefined) return null;
    const n = Number(String(valeur).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
}

const dateValide = (iso) => Boolean(dateEnToutesLettres(iso));
const lire = (iso) => new Date(`${iso}T00:00:00Z`);

/** La date limite de mise en paiement : neuf mois après la clôture. */
export function limiteMiseEnPaiement(cloture) {
    if (!dateValide(cloture)) return null;
    const d = lire(cloture);
    const limite = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + MOIS_MISE_EN_PAIEMENT + 1, 0));
    // Une clôture au dernier jour du mois donne une limite au dernier jour du
    // neuvième mois suivant ; sinon, le même quantième.
    const finDeMois = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate() === d.getUTCDate();
    if (!finDeMois) limite.setUTCDate(Math.min(d.getUTCDate(), limite.getUTCDate()));
    return limite.toISOString().slice(0, 10);
}

/** Les montants de l'affectation, lus depuis les réponses. */
export function affectationDe(r) {
    const resultat = montant(r.resultat);
    const capital = montant(r.capital);
    if (resultat === null || capital === null) return null;
    const signe = r.sens === "perte" ? -1 : 1;
    const report = montant(r.reportANouveau) ?? 0;
    return affecterResultat({
        resultat: signe * Math.abs(resultat),
        capital,
        reserveLegale: montant(r.reserveLegale) ?? 0,
        reportANouveau: r.sensReport === "perte" ? -Math.abs(report) : Math.abs(report),
        dividendes: r.sens === "perte" ? 0 : montant(r.dividendes) ?? 0,
    });
}

/**
 * Ce qui manque, ce qui est refusé, et ce qui mérite un mot sans rien bloquer.
 */
export function valider(reponses) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!FORMES_ASSOCIE_UNIQUE[r.forme]) manques.push("forme");
    for (const cle of [
        "denomination",
        "voie",
        "codePostal",
        "commune",
        "villeRcs",
        "civilite",
        "prenoms",
        "nom",
        "adresseAssocie",
        "lieuDeSignature",
    ]) {
        if (!nettoyer(r[cle])) manques.push(cle);
    }

    const numero = siren(r.siren);
    if (numero.motif === "vide") manques.push("siren");
    else if (numero.motif) invalides.push({ champ: "siren", motif: "format" });

    const capital = montant(r.capital);
    if (capital === null) manques.push("capital");
    else if (capital <= 0) invalides.push({ champ: "capital", motif: "positif" });

    if (montant(r.resultat) === null) manques.push("resultat");

    for (const cle of ["cloture", "dateDeSignature"]) {
        if (!nettoyer(r[cle])) manques.push(cle);
        else if (!dateValide(r[cle])) invalides.push({ champ: cle, motif: "format" });
    }
    if (dateValide(r.cloture) && dateValide(r.dateDeSignature) && lire(r.dateDeSignature) < lire(r.cloture)) {
        // Presque toujours, c'est la clôture qui est fausse : on saisit la fin
        // de l'exercice EN COURS au lieu de celle de l'exercice terminé. La
        // signature, elle, vaut aujourd'hui par défaut. L'erreur porte donc sur
        // la clôture, et renvoie à l'écran où elle se corrige.
        invalides.push({ champ: "cloture", motif: "apresSignature" });
    }

    const a = affectationDe(r);
    if (a && a.dividendes > 0) {
        if (!nettoyer(r.miseEnPaiement)) manques.push("miseEnPaiement");
        else if (!dateValide(r.miseEnPaiement)) invalides.push({ champ: "miseEnPaiement", motif: "format" });
        else if (r.miseEnPaiement > limiteMiseEnPaiement(r.cloture)) {
            invalides.push({ champ: "miseEnPaiement", motif: "tropTard" });
        } else if (dateValide(r.dateDeSignature) && r.miseEnPaiement < r.dateDeSignature) {
            invalides.push({ champ: "miseEnPaiement", motif: "avantDecision" });
        }
    }
    if (a?.ramene) alertes.push("dividendesRamenes");
    if (r.conventions === "oui" && !nettoyer(r.conventionsTexte)) manques.push("conventionsTexte");
    if (aLImpotSurLesSocietes(r) && !["0", "1", "2", "3"].includes(String(r.exercicesPrecedents))) {
        manques.push("exercicesPrecedents");
    }
    // Capitaux propres sous la moitié du capital : une décision distincte est
    // due dans les quatre mois (L. 223-42). Estimation par défaut : sans les
    // autres réserves, que le document ne connaît pas.
    if (a && a.reportApres < 0) {
        const cap = montant(r.capital) || 0;
        if (cap + a.reserveApres + a.reportApres < cap / 2) alertes.push("moitieCapital");
    }

    // Plus de six mois après la clôture, la décision reste possible, mais
    // hors délai : on prévient sans bloquer.
    if (dateValide(r.cloture) && dateValide(r.dateDeSignature)) {
        const six = lire(r.cloture);
        six.setUTCMonth(six.getUTCMonth() + 6);
        if (lire(r.dateDeSignature) > six) alertes.push("horsDelai");
    }

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/** Un montant pour une cellule de tableau : le symbole finit la chaîne. */
function cellule(v) {
    return `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/ /g, " ")} €`;
}

/**
 * Le document, en blocs, prêt à être posé sur une page ou dans un PDF.
 *
 * Rend `null` tant qu'il manque une réponse.
 */
export function composerDecision(reponses) {
    const r = reponses || {};
    if (!valider(r).valide) return null;

    const f = FORMES_ASSOCIE_UNIQUE[r.forme];
    const a = affectationDe(r);
    const cloture = dateEnToutesLettres(r.cloture);
    const date = dateEnToutesLettres(r.dateDeSignature);
    const feminin = r.civilite === "madame";
    const associe = `${feminin ? "Madame" : "Monsieur"} ${identite({ prenoms: r.prenoms, nom: r.nom })}`;
    // L'associée unique, quand c'est une femme : la fonction s'accorde dans
    // tout l'acte, pas seulement dans la première phrase.
    const qui = feminin ? "L'associée unique" : "L'associé unique";
    const siege = adressePostale(r);
    const resultatAbs = Math.abs(Number(montant(r.resultat)));
    const report = montant(r.reportANouveau) ?? 0;
    const reportSigne = r.sensReport === "perte" ? -Math.abs(report) : Math.abs(report);

    const blocs = [
        {
            type: "lignes",
            lignes: [
                nettoyer(r.denomination),
                `${f.forme} au capital de ${eurosEnPhrase(montant(r.capital))}`,
                `Siège social : ${siege}`,
                // Le SIREN se lit par groupes de trois, comme sur le Kbis.
                `${siren(r.siren).valeur.replace(/(\d{3})(?=\d)/g, "$1 ")} RCS ${nettoyer(r.villeRcs)}`,
            ],
        },
        {
            type: "paragraphe",
            texte:
                `Le ${date}, ${associe}, demeurant ${nettoyer(r.adresseAssocie)}, ` +
                `associ${feminin ? "ée" : "é"} unique de la société, après avoir pris connaissance ` +
                `des comptes annuels de l'exercice clos le ${cloture}, a pris les décisions suivantes.`,
        },
        { type: "decision", texte: "Première décision." },
        {
            type: "paragraphe",
            texte:
                `${qui} approuve les comptes annuels de l'exercice clos le ${cloture}, tels qu'ils ont été établis, ` +
                `qui font apparaître ${a.perte ? "une perte" : "un bénéfice"} de ${eurosEnPhrase(resultatAbs)}.`,
        },
        { type: "decision", texte: "Deuxième décision." },
    ];

    // Le report à nouveau se dit selon son signe : un solde débiteur ne
    // s'écrit pas avec un moins devant, dans un acte.
    const soldeReport = (v) =>
        v < 0
            ? `dont le solde débiteur s'élève ainsi à ${eurosEnPhrase(-v)}`
            : v === 0
              ? "qui est ainsi soldé"
              : `qui s'élève ainsi à ${eurosEnPhrase(v)}`;

    if (a.perte || resultatAbs + reportSigne <= 0) {
        blocs.push({
            type: "paragraphe",
            texte:
                `${qui} décide d'affecter ${a.perte ? "la perte" : "le bénéfice"} de l'exercice, ` +
                `soit ${eurosEnPhrase(resultatAbs)}, au compte de report à nouveau, ${soldeReport(a.reportApres)}.`,
        });
    } else {
        const origine =
            reportSigne > 0
                ? `, augmenté du report à nouveau bénéficiaire de ${eurosEnPhrase(reportSigne)}`
                : reportSigne < 0
                  ? `, diminué des pertes antérieures reportées de ${eurosEnPhrase(-reportSigne)}`
                  : "";
        const total = resultatAbs + reportSigne;
        const lignes = [];
        if (a.dotation > 0) lignes.push(["À la réserve légale", cellule(a.dotation)]);
        if (a.dividendes > 0) lignes.push([`À ${feminin ? "l'associée" : "l'associé"} unique, à titre de dividendes`, cellule(a.dividendes)]);
        if (a.reportApres > 0) lignes.push(["Au report à nouveau", cellule(a.reportApres)]);
        blocs.push(
            {
                type: "paragraphe",
                texte:
                    `${qui} décide d'affecter le bénéfice de l'exercice, soit ${eurosEnPhrase(resultatAbs)}` +
                    `${origine}, ${origine ? "formant un total de " + eurosEnPhrase(total) + ", " : ""}de la manière suivante :`,
            },
            {
                type: "tableau",
                entetes: ["Affectation", "Montant"],
                alignements: ["gauche", "droite"],
                lignes,
                total: ["Total", cellule(total)],
            },
            {
                type: "paragraphe",
                texte:
                    `Après cette affectation, la réserve légale s'élève à ${eurosEnPhrase(a.reserveApres)}` +
                    `${a.reservePleine ? ", soit le dixième du capital social" : ""}` +
                    (a.reportApres > 0
                        ? `, et le report à nouveau à ${eurosEnPhrase(a.reportApres)}.`
                        : ", et le report à nouveau est soldé.") +
                    (a.dividendes > 0
                        ? ` Les dividendes seront mis en paiement le ${dateEnToutesLettres(r.miseEnPaiement)}.`
                        : ""),
            },
        );
    }

    // L'ARTICLE 243 BIS DU CGI : toute proposition d'affectation rappelle les
    // dividendes des trois exercices précédents, la part éligible à
    // l'abattement de 40 % et la part qui ne l'est pas. Aucune formule
    // officielle n'existe ; le contenu, lui, est imposé. Pour un associé
    // personne physique, tout dividende d'une société à l'IS est éligible.
    if (aLImpotSurLesSocietes(r)) {
        const nombre = Number(r.exercicesPrecedents) || 0;
        if (nombre === 0) {
            blocs.push({
                type: "paragraphe",
                texte:
                    `Conformément à l'article 243 bis du code général des impôts, ${qui.charAt(0).toLowerCase()}${qui.slice(1)} rappelle ` +
                    "que la société n'a clos aucun exercice avant celui-ci : aucun dividende n'a été mis en distribution " +
                    "au titre des exercices précédents.",
            });
        } else {
            const cloture = new Date(`${r.cloture}T12:00:00Z`);
            const lignes = [];
            for (let i = 1; i <= nombre; i++) {
                const d = new Date(Date.UTC(cloture.getUTCFullYear() - i, cloture.getUTCMonth(), cloture.getUTCDate(), 12));
                const verse = Math.abs(montant(r[`dividendesN${i}`]) ?? 0);
                lignes.push([dateEnToutesLettres(d.toISOString().slice(0, 10)), verse > 0 ? cellule(verse) : "Néant"]);
            }
            blocs.push(
                {
                    type: "paragraphe",
                    texte:
                        `Conformément à l'article 243 bis du code général des impôts, ${qui.charAt(0).toLowerCase()}${qui.slice(1)} rappelle ` +
                        "les dividendes mis en distribution au titre des exercices précédents. Ils étaient tous éligibles " +
                        "à l'abattement de 40 % prévu au 2° du 3 de l'article 158 du même code, et aucun revenu distribué n'était inéligible à cet abattement.",
                },
                {
                    type: "tableau",
                    entetes: ["Exercice clos le", "Dividendes distribués"],
                    alignements: ["gauche", "droite"],
                    lignes,
                },
            );
        }
    }

    blocs.push({ type: "decision", texte: "Troisième décision." });
    blocs.push({
        type: "paragraphe",
        texte:
            r.conventions === "oui"
                ? `${qui} prend acte des conventions suivantes, intervenues au cours de l'exercice ` +
                  `entre la société et son ${f.dirigeant} : ${nettoyer(r.conventionsTexte)}.`
                : `${qui} constate qu'aucune convention n'est intervenue au cours de l'exercice ` +
                  `entre la société et son ${f.dirigeant}, directement ou par personne interposée.`,
    });

    blocs.push({ type: "decision", texte: "Quatrième décision." });
    blocs.push({
        type: "paragraphe",
        texte:
            `${qui} donne tous pouvoirs au porteur d'un original ou d'une copie des présentes ` +
            "pour accomplir les formalités de dépôt et de publicité.",
    });
    blocs.push({
        type: "paragraphe",
        texte:
            "Les présentes décisions sont consignées au registre des décisions de la société. " +
            `Fait à ${nettoyer(r.lieuDeSignature)}, le ${date}.`,
    });
    blocs.push({ type: "signature", libelle: qui, nom: identite({ prenoms: r.prenoms, nom: r.nom }) });

    // Chaque décision ouvre son propre paragraphe : « Première décision. »
    // suivi du texte, comme dans un procès-verbal. Un titre sur sa ligne
    // coûtait quatre blocs et une seconde page.
    const fusionnes = [];
    for (const bloc of blocs) {
        const precedent = fusionnes[fusionnes.length - 1];
        if (precedent?.type === "decision") {
            fusionnes[fusionnes.length - 1] = { ...bloc, texte: `${precedent.texte} ${bloc.texte}` };
        } else {
            fusionnes.push(bloc);
        }
    }

    return {
        titre: `Décisions de l'associé unique du ${date}`,
        blocs: fusionnes,
    };
}
